import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { IItem, IPurchaseRequisition } from "@/types/purchaseRequisition.type";
import {
  Button,
  Card,
  Modal,
  Label,
  Select,
  FileInput,
  Checkbox,
} from "flowbite-react";
import { useAttachFilePresignedUrls, useGetAllSuppliers } from "@/common/hooks";
import React, { useState } from "react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  DraftOrder,
  validatePurchaseRequisition,
} from "@/common/services/purchaseRequisition.api";
import { uploadFilePresignedUrl } from "@/common/services/purchaseOrder.api";
import { ApprovedRow } from "./ModalValidatePurchaseRequisition";

export const ModalQuotedPurchaseRequisition: React.FC<{
  requisition: IPurchaseRequisition & { budgets: DraftOrder[] };
  onClose: () => void;
}> = ({ requisition, onClose }) => {
  const { data: suppliers = [] } = useGetAllSuppliers();
  const [loading, setLoading] = useState(false);
  const { notifyError, notifySuccess } = useToastContext();
  const { mutateAsync } = useAttachFilePresignedUrls();

  const initialValues =
    requisition.budgets.length > 0
      ? requisition.budgets
      : [
          {
            supplierId: "",
            supplierName: "",
            items: [],
            files: [],
            deliveryDate: "",
          },
        ];

  const [draftOrders, setDraftOrders] = useState<DraftOrder[]>(initialValues);

  const handleSupplierChange = (
    orderIndex: number,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const supplierId = e.target.value;
    const supplierName = e.target.options[e.target.selectedIndex].text;
    setDraftOrders((prev) =>
      prev.map((draft, i) =>
        i === orderIndex ? { ...draft, supplierId, supplierName } : draft
      )
    );
  };

  const handleFileChange = (orderIndex: number, files: File[] | FileList) => {
    if (!files || files.length === 0) return;
    setDraftOrders((prev) =>
      prev.map((draft, i) => (i === orderIndex ? { ...draft, files } : draft))
    );
  };

  const addDraftOrder = () => {
    setDraftOrders((prev) => [
      ...prev,
      {
        supplierId: "",
        supplierName: "",
        items: [],
        files: [],
        deliveryDate: "",
      },
    ]);
  };

  const removeDraftOrder = (orderIndex: number) => {
    setDraftOrders((prev) => prev.filter((_, i) => i !== orderIndex));
  };

  const toggleItemInOrder = (
    orderIndex: number,
    item: IItem,
    checked: boolean
  ) => {
    setDraftOrders((prev) => {
      const newDrafts = structuredClone(prev);

      if (!checked) {
        newDrafts[orderIndex].items = newDrafts[orderIndex].items.filter(
          (record) => record.productId !== item.productId
        );
        return newDrafts;
      }

      newDrafts.forEach((draft, i) => {
        if (i !== orderIndex) {
          draft.items = draft.items.filter(
            (record) => record.productId !== item.productId
          );
        }
      });

      newDrafts[orderIndex].items.push({
        productId: item.productId,
        productName: item.productName,
        approvedQuantity: String(item.quantity),
        quoted: (item as unknown as ApprovedRow).quoted,
        quoteValue: undefined,
        deliveryDate: item.deliveryDate ? String(item.deliveryDate) : undefined,
      });

      return newDrafts;
    });
  };

  const handleQuantityChange = (
    orderIndex: number,
    productId: string,
    quantity: number
  ) => {
    setDraftOrders((prev) => {
      const newDrafts = structuredClone(prev);
      const target = newDrafts[orderIndex].items.find(
        (i) => i.productId === productId
      );
      if (target) target.approvedQuantity = String(quantity);
      return newDrafts;
    });
  };

  const handleQuotedValueChange = (
    orderIndex: number,
    productId: string,
    value: string
  ) => {
    setDraftOrders((prev) => {
      const newDrafts = structuredClone(prev);
      const target = newDrafts[orderIndex].items.find(
        (i) => i.productId === productId
      );
      if (target) target.quoteValue = value;
      return newDrafts;
    });
  };

  const handleDeliveryDateChange = (orderIndex: number, date: string) => {
    setDraftOrders((prev) => {
      const newDrafts = structuredClone(prev);
      const target = newDrafts[orderIndex];
      if (target) target.deliveryDate = date;
      return newDrafts;
    });
  };

  const onSave = async () => {
    setLoading(true);
    try {
      const draftsToSend: DraftOrder[] = [];

      for (const draft of draftOrders) {
        const hasQuotedItems = draft.items.some((i) => i.quoted);
        if (hasQuotedItems) continue;

        let presignedResponse;

        if (draft?.files?.length > 0) {
          try {
            presignedResponse = await mutateAsync(draft.files as File[]);
          } catch {
            notifyError("Erro ao processar arquivo");
            continue;
          }
        }

        if (!presignedResponse?.isPresigned) {
          notifyError("Erro ao gerar URLs presignadas");
          continue;
        }

        const urls = presignedResponse.urls;

        if (!urls || urls.length === 0) {
          notifyError("Erro ao enviar arquivos");
          continue;
        }

        for (let i = 0; i < draft?.files?.length; i++) {
          const file = draft?.files[i];
          const url = urls[i]?.url;

          if (!url) {
            notifyError(
              `Erro ao encontrar URL para o arquivo ${(file as File).name}`
            );
            continue;
          }

          await uploadFilePresignedUrl(file as File, url);
        }

        const files = normalizeFiles(draft.files);

        draftsToSend.push({
          supplierId: draft.supplierId,
          supplierName: draft.supplierName,
          items: draft.items.map((item) => ({
            ...item,
            quoted: true,
          })),
          files: files.map((f) => f.name),
          deliveryDate: draft.deliveryDate,
        });
      }

      if (draftsToSend.length === 0) {
        notifyError("Nenhum orçamento novo para enviar");
        return;
      }

      const result = await validatePurchaseRequisition(
        requisition._id,
        draftsToSend
      );

      if (result.ok) {
        notifySuccess("Orçamento(s) gerado(s) com sucesso.");
        onClose();
      } else {
        notifyError(result.message || "Erro ao gerar orçamento de compra");
      }
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const allAssignedProductIds = new Set(
    draftOrders.flatMap((d) => d.items.map((record) => record.productId))
  );

  return (
    <Modal show={true} onClose={onClose} size="7xl">
      <Modal.Header>
        <h2 className="text-black">Orçamento ordem de compra</h2>
      </Modal.Header>
      <Modal.Body>
        {draftOrders.map((order, orderIndex) => {
          const isQuoted = order.items.some((item) => item.quoted);

          return (
            <Card
              key={orderIndex}
              className={`mb-4 transition-opacity ${
                isQuoted ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex gap-10">
                  <h3 className="font-bold text-lg">
                    Orçamento #{orderIndex + 1}
                  </h3>
                  <input
                    type="date"
                    className="w-40 border border-gray-300 px-3 py-2 rounded-md"
                    value={
                      order.deliveryDate
                        ? String(order.deliveryDate).slice(0, 10)
                        : ""
                    }
                    onChange={(e) =>
                      handleDeliveryDateChange(orderIndex, e.target.value)
                    }
                    disabled={isQuoted}
                  />
                  <label>Previsão de entrega</label>
                </div>
                {!isQuoted && draftOrders.length > 1 && (
                  <Button
                    color="failure"
                    size="xs"
                    onClick={() => removeDraftOrder(orderIndex)}
                  >
                    Remover
                  </Button>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`supplier-select-${orderIndex}`} color="black">
                  Fornecedor
                </Label>
                <Select
                  color="black"
                  id={`supplier-select-${orderIndex}`}
                  value={order.supplierId}
                  onChange={(e) => handleSupplierChange(orderIndex, e)}
                  disabled={isQuoted}
                >
                  <option value="">Selecione um fornecedor</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.companyName}
                    </option>
                  ))}
                </Select>
              </div>

              <p className="mt-3 font-semibold">Itens:</p>
              <div className="flex flex-col gap-1">
                {requisition.itens.map((item) => {
                  const isChecked = order.items.some(
                    (i) => i.productId === item.productId
                  );
                  const isDisabled =
                    !isChecked && allAssignedProductIds.has(item.productId);
                  const currentItem = order.items.find(
                    (i) => i.productId === item.productId
                  );

                  return (
                    <div
                      key={item.productId}
                      className="grid grid-cols-2 items-center gap-4 py-2 border-t first:border-t-0"
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          color="black"
                          id={`item-check-${orderIndex}-${item.productId}`}
                          checked={isChecked}
                          disabled={isDisabled || isQuoted}
                          onChange={(e) =>
                            toggleItemInOrder(
                              orderIndex,
                              item,
                              e.target.checked
                            )
                          }
                        />
                        <Label
                          color="black"
                          htmlFor={`item-check-${orderIndex}-${item.productId}`}
                          className={
                            isDisabled || isQuoted ? "text-gray-400" : ""
                          }
                        >
                          {item.productName}
                        </Label>
                      </div>
                      {isChecked && (
                        <div className="flex justify-end gap-2">
                          <input
                            type="number"
                            min="1"
                            value={currentItem?.approvedQuantity ?? 0}
                            onChange={(e) =>
                              handleQuantityChange(
                                orderIndex,
                                item.productId,
                                Number(e.target.value)
                              )
                            }
                            className="w-32 border border-gray-300 px-3 py-2 rounded-md text-right"
                            placeholder="Qtd."
                            disabled={isQuoted}
                          />
                          <input
                            type="number"
                            inputMode="decimal"
                            value={currentItem?.quoteValue ?? ""}
                            onChange={(e) =>
                              handleQuotedValueChange(
                                orderIndex,
                                item.productId,
                                e.target.value
                              )
                            }
                            className="w-28 border border-gray-300 px-3 py-2 rounded-md text-right"
                            placeholder="R$"
                            disabled={isQuoted}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <Label
                    color="black"
                    htmlFor={`nf-file-upload-${orderIndex}`}
                    value="Anexar Orçamentos"
                  />
                  <FileInput
                    color="black"
                    multiple={true}
                    id={`nf-file-upload-${orderIndex}`}
                    onChange={(e) =>
                      handleFileChange(
                        orderIndex,
                        e.target.files as unknown as File[]
                      )
                    }
                    disabled={isQuoted}
                  />
                </div>
              </div>
            </Card>
          );
        })}

        <Button onClick={addDraftOrder} color="red" size="sm">
          Adicionar outra Ordem de Compra
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          Fechar
        </Button>
        <ButtonWithLoader onClick={onSave} disabled={loading}>
          {loading ? "Processando..." : "Salvar"}
        </ButtonWithLoader>
      </Modal.Footer>
    </Modal>
  );
};

function normalizeFiles(
  input: FileList | File[] | string[] | undefined
): File[] {
  if (!input) return [];

  if (input instanceof FileList) {
    return Array.from(input);
  }

  if (Array.isArray(input) && input.length > 0 && input[0] instanceof File) {
    return input as File[];
  }

  return [];
}
